import { Loading3QuartersOutlined, RightOutlined } from "@ant-design/icons";
import { Button } from "@dreambigger/design-system/src/components";
import { CustomCollectionsStepAssets } from "@dreambigger/shared/src/types";
import { Spin } from "antd";
import { useSegment } from "@dreambigger/shared/src/hooks";
import { StepContentSection, StepFooterText } from "../../components";
import { StepProps } from "../../pages/flows/[flowId]";
import { useApplication } from "../../api";
import { prefixFieldsWithSlug } from "../../utils/prefixStepSlug";
import useCaptureScreenshot from "../../hooks/use-capture-screenshot";
import compileTemplate from "../../utils/compileTemplate";
import BeforeHooksGate from "./components/BeforeHooksGate";
import EntryCard from "./components/EntryCard";
import useBeforeHooks from "./hooks/use-before-hooks";
import useStepLogic from "./hooks/useStepLogic";
import entriesToResponseFields from "./utils/entriesToResponseFields";

export default function CustomCollections({
  flow,
  step,
  brand,
  progress,
}: StepProps) {
  const assets = step.assets as CustomCollectionsStepAssets;
  const { slug, type } = step;
  const segment = useSegment();

  // Screenshot capture wraps the entire step render; the captureScreenshot
  // callback is passed to the step-logic hook so handleNext can snapshot
  // before navigation. Same shared hook step-wrapper uses.
  const { screenshotAreaRef, captureScreenshot } = useCaptureScreenshot(
    flow.financialInstitution.id
  );

  const applicationHelper = useApplication(flow.financialInstitution.id);
  const { beforeHooksLoaded, beforeHooksError } = useBeforeHooks({
    assets,
    application: applicationHelper.find(flow.id, "draft"),
    applicationHelper,
  });

  const {
    entries,
    saving,
    initialized,
    isValid,
    validationMessage,
    expandedEntryId,
    setExpandedEntryId,
    addEntry,
    removeEntry,
    saveEntry,
    handleNext,
    canAddEntry,
    getUniqueConflict,
    getCrossEntryTotal,
    getCrossEntryCount,
    application,
    displayConfig: dc,
    effectiveValidations,
  } = useStepLogic({ flow, slug, type, assets, captureScreenshot });

  // Gate render until beforeHooks resolve — matches StepWrapper's UX.
  if (assets.beforeHooks && (!beforeHooksLoaded || beforeHooksError)) {
    return (
      <BeforeHooksGate
        assets={assets}
        brand={brand}
        progress={progress}
        loaded={beforeHooksLoaded}
        error={beforeHooksError}
      >
        <></>
      </BeforeHooksGate>
    );
  }

  if (!initialized) {
    return (
      <div className="flex justify-center pv-6">
        <Spin indicator={<Loading3QuartersOutlined spin />} />
      </div>
    );
  }

  const onNext = async () => {
    try {
      await handleNext();
    } finally {
      // Match step-wrapper.defaultHandleNext semantics: fire Submit
      // tracking in `finally` so failed saves still emit. Properties are
      // prefixed with slug (`slug__fieldKey`) matching v1 shape; the per-entry
      // index is embedded in the key suffix by entriesToResponseFields, which
      // diverges from v1 (v1 has no repeats).
      const fields = entriesToResponseFields(entries);
      segment.track({
        action: "Button Click",
        label: `Submit - ${slug}`,
        properties: prefixFieldsWithSlug({ fields, slug }),
      });
    }
  };

  // Split entries into pinned (auto-populated) and unpinned
  const pinnedEntries = entries.filter((e) => e._autoPopIndex !== undefined);
  const unpinnedEntries = entries.filter((e) => e._autoPopIndex === undefined);

  return (
    <div ref={screenshotAreaRef}>
      <StepContentSection
        brand={brand}
        stepProgress={progress}
        title={assets.title || ""}
        description={assets.description || ""}
        previousSlug={assets.previousSlug}
        hideProgressBar={assets.hideProgressBar}
        heroImage={assets.heroImage}
        heroText={assets.heroText}
        heroLogo={assets.heroLogo}
      >
        {pinnedEntries.map((entry) => (
          <EntryCard
            key={entry.id}
            entry={entry}
            customFields={assets.customFields}
            crossEntryValidations={effectiveValidations}
            brand={brand}
            application={application}
            autoPopConfig={
              entry._autoPopIndex !== undefined
                ? assets.autoPopulate?.[entry._autoPopIndex]
                : undefined
            }
            isExpanded={expandedEntryId === entry.id}
            onExpand={() => setExpandedEntryId(entry.id)}
            onCollapse={() => setExpandedEntryId(null)}
            onSave={async (data) => saveEntry(entry.id, data)}
            displayConfig={dc}
            getUniqueConflict={getUniqueConflict}
            getCrossEntryTotal={getCrossEntryTotal}
            getCrossEntryCount={getCrossEntryCount}
          />
        ))}

        {unpinnedEntries.map((entry) => (
          <EntryCard
            key={entry.id}
            entry={entry}
            customFields={assets.customFields}
            crossEntryValidations={effectiveValidations}
            brand={brand}
            application={application}
            isExpanded={expandedEntryId === entry.id}
            onExpand={() => setExpandedEntryId(entry.id)}
            onCollapse={() => setExpandedEntryId(null)}
            onSave={async (data) => saveEntry(entry.id, data)}
            onDelete={() => removeEntry(entry.id)}
            onCancel={() => {
              // Discard the entry if the user cancels before it has enough
              // data to render a card title (typical "Add then Cancel" flow).
              if (!compileTemplate(dc.entryHeader.title, entry)) {
                removeEntry(entry.id);
              } else {
                setExpandedEntryId(null);
              }
            }}
            displayConfig={dc}
            getUniqueConflict={getUniqueConflict}
            getCrossEntryTotal={getCrossEntryTotal}
            getCrossEntryCount={getCrossEntryCount}
          />
        ))}

        {canAddEntry && (
          <Button
            type="link"
            onClick={() => addEntry()}
            className="pl-0 mt-1"
            style={{ color: brand.primaryColor }}
          >
            {dc.entryAddText}
          </Button>
        )}

        {(assets.disclaimerTitle || assets.disclaimerText) && (
          <div className="mt-4">
            {assets.disclaimerTitle && (
              <h3
                className="fwt"
                dangerouslySetInnerHTML={{ __html: assets.disclaimerTitle }}
              />
            )}
            {assets.disclaimerText && (
              <p
                className="f-4 gray-8 mb-0 lh-3"
                dangerouslySetInnerHTML={{ __html: assets.disclaimerText }}
              />
            )}
          </div>
        )}

        <div className="lg_mt-6 pt-3 lg_pt-4">
          {!isValid && validationMessage && (
            <p className="f-2 red-1 mb-2">{validationMessage}</p>
          )}
          <Button
            type="primary"
            onClick={onNext}
            loading={saving}
            disabled={!isValid}
            className="bn br-0 lift h-7 ph-5"
          >
            {assets.submitButtonText || "Next"} <RightOutlined />
          </Button>
          <StepFooterText text={assets.stepFooterText} />
        </div>
      </StepContentSection>
    </div>
  );
}
